import React, {
  ButtonHTMLAttributes,
  FC,
  HTMLAttributes,
  ReactNode,
} from 'react';
import {Icon, IconName} from './Icon';
import {cx} from './cx';

export const Brand: FC<{className?: string}> = ({className}) => (
  <span className={cx('sui-brand', className)}>
    <img
      alt=""
      aria-hidden="true"
      src="/images/souhait/brand/mark-transparent.png"
      width={36}
      height={36}
    />
    <span>souha.it</span>
  </span>
);

type HeadingLevel = 1 | 2 | 3 | 4;

interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  display?: boolean;
}

export const Heading: FC<HeadingProps> = ({
  level = 2,
  display = false,
  className,
  children,
  ...props
}) => {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4';
  return (
    <Tag
      className={cx('sui-heading', display && 'sui-heading--display', className)}
      {...props}
    >
      {children}
    </Tag>
  );
};

interface TextProps extends HTMLAttributes<HTMLParagraphElement> {
  tone?: 'default' | 'muted' | 'soft';
  size?: 'sm' | 'md' | 'lg';
}

export const Text: FC<TextProps> = ({
  tone = 'default',
  size = 'md',
  className,
  children,
  ...props
}) => (
  <p
    className={cx(
      'sui-text',
      `sui-text--${tone}`,
      `sui-text--${size}`,
      className
    )}
    {...props}
  >
    {children}
  </p>
);

export const Eyebrow: FC<HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  children,
  ...props
}) => (
  <p className={cx('sui-eyebrow', className)} {...props}>
    {children}
  </p>
);

interface PaperProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'flat' | 'raised' | 'pinned' | 'tinted';
}

export const Paper: FC<PaperProps> = ({
  variant = 'flat',
  className,
  children,
  ...props
}) => (
  <div
    className={cx('sui-paper', `sui-paper--${variant}`, className)}
    {...props}
  >
    {children}
  </div>
);

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'terracotta' | 'mustard' | 'leaf' | 'sky';
  icon?: IconName;
}

export const Badge: FC<BadgeProps> = ({
  tone = 'neutral',
  icon,
  className,
  children,
  ...props
}) => (
  <span
    className={cx('sui-badge', `sui-badge--${tone}`, className)}
    {...props}
  >
    {icon && <Icon name={icon} size={14} />}
    {children}
  </span>
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'paper';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  iconStart?: IconName;
  iconEnd?: IconName;
  loading?: boolean;
}

export const Button: FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  iconStart,
  iconEnd,
  loading = false,
  className,
  children,
  disabled,
  ...props
}) => (
  <button
    className={cx(
      'sui-button',
      `sui-button--${variant}`,
      `sui-button--${size}`,
      fullWidth && 'sui-button--full',
      loading && 'is-loading',
      className
    )}
    disabled={disabled || loading}
    {...props}
  >
    {loading ? (
      <span aria-hidden="true" className="sui-spinner sui-spinner--small" />
    ) : (
      iconStart && <Icon name={iconStart} size={18} />
    )}
    <span>{children}</span>
    {iconEnd && !loading && <Icon name={iconEnd} size={18} />}
  </button>
);

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string;
  variant?: 'primary' | 'paper' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const IconButton: FC<IconButtonProps> = ({
  icon,
  label,
  variant = 'paper',
  size = 'md',
  className,
  ...props
}) => (
  <button
    aria-label={label}
    className={cx(
      'sui-icon-button',
      `sui-icon-button--${variant}`,
      `sui-icon-button--${size}`,
      className
    )}
    title={label}
    {...props}
  >
    <Icon name={icon} size={size === 'sm' ? 16 : size === 'lg' ? 22 : 19} />
  </button>
);

const motifPaths = {
  arrow: '/images/souhait/motifs/arrow.png',
  bow: '/images/souhait/motifs/bow.png',
  confetti: '/images/souhait/motifs/confetti.png',
  flower: '/images/souhait/motifs/flower.png',
  gift: '/images/souhait/motifs/gift.png',
  heart: '/images/souhait/motifs/heart.png',
  leaf: '/images/souhait/motifs/leaf.png',
  sparkle: '/images/souhait/motifs/sparkle.png',
};

export type MotifName = keyof typeof motifPaths;

interface MotifProps {
  name: MotifName;
  size?: number;
  className?: string;
  decorative?: boolean;
  alt?: string;
}

export const Motif: FC<MotifProps> = ({
  name,
  size = 72,
  className,
  decorative = true,
  alt = '',
}) => (
  <img
    alt={decorative ? '' : alt}
    aria-hidden={decorative}
    className={cx('sui-motif', className)}
    height={size}
    src={motifPaths[name]}
    width={size}
  />
);

export const Divider: FC<{children?: ReactNode; className?: string}> = ({
  children,
  className,
}) => (
  <div className={cx('sui-divider', className)}>
    {children && <span>{children}</span>}
  </div>
);
